import api from './api';

/**
 * Admin-Service: Informationsseiten + KI-Inhalts-Sync.
 * Exakt nach dem Vorbild von adminProRankingsService.
 *
 * CRUD: /api/admin/info-pages, /api/admin/info-page/id/:id
 * Sync (Quellen/Settings/Läufe): /api/admin/info-page-sync/*
 * 4xx-Fehler kommen als Plaintext (getErrorMessage nutzen).
 */

export interface InfoPageSection {
  heading?: string | null;
  body?: string | null;
  order?: number | null;
}

export interface InfoPageFaq {
  question?: string | null;
  answer?: string | null;
  order?: number | null;
}

export interface InfoPageKeyFact {
  icon?: string | null;
  label?: string | null;
  value?: string | null;
  sublabel?: string | null;
  order?: number | null;
}

export interface InfoPageAdmin {
  _id?: string | null;
  id?: string | null;
  slug?: string | null;
  title?: string | null;
  category?: string | null;
  adminPrompt?: string | null;
  intro?: string | null;
  sections?: InfoPageSection[] | null;
  faq?: InfoPageFaq[] | null;
  keyFacts?: InfoPageKeyFact[] | null;
  thumbnail?: string | null;
  heroImage?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string[] | null;
  tags?: string[] | null;
  visible?: boolean | null;
  sortOrder?: number | null;
  views?: number | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
  createdAt?: number | null;
}

export interface InfoPageSyncSource {
  id?: string | null;
  infoPageId?: string | null;
  name?: string | null;
  url?: string | null;
  enabled?: boolean | null;
  lastCheckedAt?: number | null;
  lastError?: string | null;
  createdAt?: number | null;
}

export interface InfoPageSyncSettings {
  enabled?: boolean | null;
  intervalMinutes?: number | null;
  claudeModel?: string | null;
  aiBanners?: boolean | null;
  lastRunAt?: string | number | null;
}

export interface InfoPageSyncRunResult {
  infoPageId?: string | null;
  slug?: string | null;
  title?: string | null;
  sourcesUsed?: number | null;
  updatedFields?: string[] | null;
  bannerGenerated?: boolean | null;
  error?: string | null;
}

export interface InfoPageSyncRun {
  id?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  status?: 'running' | 'success' | 'error' | string | null;
  triggeredBy?: string | null;
  pagesProcessed?: number | null;
  results?: InfoPageSyncRunResult[] | null;
  errors?: string[] | null;
}

class AdminInfoPagesService {
  // ---------- Seiten ----------

  async listPages(): Promise<InfoPageAdmin[]> {
    const response = await api.get('/api/admin/info-pages');
    return (response.data?.pages as InfoPageAdmin[]) ?? [];
  }

  async createPage(payload: {
    title: string;
    category?: string;
    adminPrompt?: string;
  }): Promise<InfoPageAdmin> {
    const response = await api.post('/api/admin/info-pages', payload);
    return response.data as InfoPageAdmin;
  }

  async updatePage(id: string, payload: Partial<InfoPageAdmin>): Promise<InfoPageAdmin> {
    const response = await api.put(`/api/admin/info-page/id/${id}`, payload);
    return response.data as InfoPageAdmin;
  }

  async deletePage(id: string): Promise<void> {
    await api.delete(`/api/admin/info-page/id/${id}`);
  }

  // ---------- Sync-Quellen ----------

  async listSources(pageId?: string): Promise<InfoPageSyncSource[]> {
    const response = await api.get('/api/admin/info-page-sync/sources', {
      params: pageId ? { pageId } : undefined,
    });
    return (response.data?.sources as InfoPageSyncSource[]) ?? [];
  }

  async createSource(payload: {
    infoPageId: string;
    url: string;
    name?: string;
    enabled?: boolean;
  }): Promise<InfoPageSyncSource> {
    const response = await api.post('/api/admin/info-page-sync/sources', payload);
    return (response.data?.source as InfoPageSyncSource) ?? {};
  }

  async updateSource(id: string, payload: Partial<{
    name: string;
    url: string;
    enabled: boolean;
  }>): Promise<InfoPageSyncSource> {
    const response = await api.put(`/api/admin/info-page-sync/source/${id}`, payload);
    return (response.data?.source as InfoPageSyncSource) ?? {};
  }

  async deleteSource(id: string): Promise<void> {
    await api.delete(`/api/admin/info-page-sync/source/${id}`);
  }

  // ---------- Einstellungen ----------

  async getSettings(): Promise<InfoPageSyncSettings> {
    const response = await api.get('/api/admin/info-page-sync/settings');
    return (response.data?.settings as InfoPageSyncSettings) ?? {};
  }

  async updateSettings(payload: Partial<{
    enabled: boolean;
    intervalMinutes: number;
    claudeModel: string;
    aiBanners: boolean;
  }>): Promise<InfoPageSyncSettings> {
    const response = await api.put('/api/admin/info-page-sync/settings', payload);
    return (response.data?.settings as InfoPageSyncSettings) ?? {};
  }

  // ---------- Läufe ----------

  /** Lauf starten – optional nur für EINE Seite (pageId). 409 wenn schon aktiv. */
  async triggerRun(pageId?: string): Promise<void> {
    await api.post('/api/admin/info-page-sync/run', pageId ? { pageId } : {});
  }

  async listRuns(limit = 20): Promise<{ runs: InfoPageSyncRun[]; total: number }> {
    const response = await api.get('/api/admin/info-page-sync/runs', { params: { limit } });
    return {
      runs: (response.data?.runs as InfoPageSyncRun[]) ?? [],
      total: response.data?.total ?? 0,
    };
  }

  // ---------- Banner ----------

  /** Lädt ein Bild hoch (POST /api/file, ROHES Base64) und liefert die /api/file/-URL. */
  async uploadBannerImage(file: File): Promise<string> {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl;
    const response = await api.post('/api/file', { data: base64 });
    const name = response.data?.name;
    if (!name) throw new Error('Upload fehlgeschlagen');
    return `/api/file/${name}`;
  }

  /** Eigenes Banner setzen (ersetzt KI-Banner). */
  async setPageBanner(id: string, url: string): Promise<InfoPageAdmin> {
    return this.updatePage(id, { thumbnail: url, heroImage: url });
  }

  /** Banner entfernen (KI generiert beim nächsten Lauf ggf. ein neues). */
  async removePageBanner(id: string): Promise<InfoPageAdmin> {
    return this.updatePage(id, { thumbnail: null, heroImage: null });
  }

  // ---------- Helper ----------

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: unknown } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    return fallback;
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }
}

const adminInfoPagesService = new AdminInfoPagesService();
export default adminInfoPagesService;
